import React, {
  createContext,
  useContext,
  useCallback,
  ReactNode,
  useRef,
} from "react";
import { Irai, MasterItem, MasterStatus } from "../types/GxpTypes";
import { useAuth } from 'react-oidc-context';
import { apiGet, handleApiError } from './ApiClient';
import { useToast } from './ToastContext';

type DataContextProps = {
  iraiData: Irai[];
  refreshData: (force?: boolean) => void;
  removeIraiFromCache: (iraiId: string) => void;
  getMaster: (fieldName: keyof Irai) => MasterItem[];
};

type AllMastersResponse = {
  statusMaster: MasterStatus[];
  lymphStatusMaster: MasterItem[];
  diagnosisMaster: MasterItem[];
  receptorStatusMaster: MasterItem[];
  her2StatusMaster: MasterItem[];
  tClassMaster: MasterItem[];
  nClassMaster: MasterItem[];
  mClassMaster: MasterItem[];
  menstruationMaster: MasterItem[];
  tumorGradeMaster: MasterItem[];
  biopsyMaster: MasterItem[];
};

const DataContext = createContext<DataContextProps | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};

export const DataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const auth = useAuth(); // authオブジェクトを取得
  const { showToast } = useToast(); // Toast表示機能を取得
  // バックエンドデータをuseRefで管理
  const irais = useRef<Irai[]>([]);
  const statusMaster = useRef<MasterStatus[]>([]);
  const lympthMaster = useRef<MasterItem[]>([]);
  const diagnosisMaster = useRef<MasterItem[]>([]);
  const her2StatusMaster = useRef<MasterItem[]>([]);
  const genderMaster = useRef<MasterItem[]>([]);
  const erMaster = useRef<MasterItem[]>([]);
  const pgrMaster = useRef<MasterItem[]>([]);
  const tClassMaster = useRef<MasterItem[]>([]);
  const nClassMaster = useRef<MasterItem[]>([]);
  const mClassMaster = useRef<MasterItem[]>([]);
  const menstruationMaster = useRef<MasterItem[]>([]);
  const tumorGradeMaster = useRef<MasterItem[]>([]);
  const biopsyMaster = useRef<MasterItem[]>([]);
  // データ初期化完了フラグ
  // 重要！　アプリ起動時に初期化完了後trueにセットされ、以降は値が変更されない
  const isRefreshPerformedOnce = useRef<boolean>(false);

  // 実行制御フラグ
  // 再レンダリングを強制するためのカウンター
  const [, setUpdateCounter] = React.useState<number>(0);
  const isRefreshingRef = useRef<boolean>(false);


  const refresh = useCallback(async (force: boolean = false) => {
    // refresh実行中の場合は重複実行を防ぐ
    if (isRefreshingRef.current) {
      return;
    }

    // 強制更新フラグがfalseで、既にデータが初期化されている場合は再実行しない
    if (!force && isRefreshPerformedOnce.current) {
      return;
    }

    isRefreshingRef.current = true; // 初回データ取得(init)開始フラグを設定

    try {
      isRefreshPerformedOnce.current = true;
      try {
        const { data: allMasters } = await apiGet<AllMastersResponse>("/getAllMasters", auth.user?.access_token);
        statusMaster.current = [...allMasters.statusMaster];
        lympthMaster.current = [...allMasters.lymphStatusMaster];
        diagnosisMaster.current = [...allMasters.diagnosisMaster];
        erMaster.current = [...allMasters.receptorStatusMaster];
        pgrMaster.current = [...allMasters.receptorStatusMaster];
        her2StatusMaster.current = [...allMasters.her2StatusMaster];
        tClassMaster.current = [...allMasters.tClassMaster];
        nClassMaster.current = [...allMasters.nClassMaster];
        mClassMaster.current = [...allMasters.mClassMaster];
        menstruationMaster.current = [...allMasters.menstruationMaster];
        tumorGradeMaster.current = [...allMasters.tumorGradeMaster];
        biopsyMaster.current = [...allMasters.biopsyMaster];
        genderMaster.current = [{ id: 1, name: '男性' }, { id: 2, name: '女性' }];
      } catch (error) {
        console.error("Error fetching status master:", handleApiError(error, 'Fetching status master'));
        if (error instanceof TypeError) {
          showToast(error.message, 'error');
        }
        statusMaster.current = [];
        lympthMaster.current = [];
        diagnosisMaster.current = [];
        erMaster.current = [];
        pgrMaster.current = [];
        her2StatusMaster.current = [];
        tClassMaster.current = [];
        nClassMaster.current = [];
        mClassMaster.current = [];
        menstruationMaster.current = [];
        tumorGradeMaster.current = [];
        biopsyMaster.current = [];
        genderMaster.current = [];
        return; // エラーが発生した場合は処理を中断
      }

      if (!auth.user || !auth.user.profile) {
        showToast("ユーザーが認証されていません。", 'error');
        return; // ユーザーが認証されていない場合は処理を中断
      }

      const email = auth.user.profile.email;
      const orgId = auth.user.profile['custom:organization_id'] || '';
      const isemergency = auth.user.profile['custom:isemergency'] || "0";
      const ispoweruser = auth.user.profile['custom:ispoweruser'] || "0";
      let paramString = '?organizationId=' + orgId;
      if (isemergency !== "1" && ispoweruser !== "1") {
        // 普通ユーザーの場合そのユーザが承認権限持つデータのみを取得する
        paramString += `&approvalDoctorId=${email}`;
      }

      try {
        // APIから依頼データを取得
        const { data: fetchIraiDataResult } = await apiGet<Irai[]>("/getIrais" + paramString, auth.user?.access_token);
        if (!fetchIraiDataResult || !Array.isArray(fetchIraiDataResult)) {
          irais.current = [];
        } else {
          irais.current = [...fetchIraiDataResult];
        }

      } catch (error) {
        console.error("Error fetching irai data:", handleApiError(error, 'Fetching irai data'));
        if (error instanceof TypeError) {
          showToast(error.message, 'error');
        }
        irais.current = [];
      }

      // 表示するために必要なフィールドを加工・追加
      const mergedIraiData = irais.current.map((irai) => {
        //patientNameフィールドをpatientLastNameとpatientFirstNameから生成
        irai.patientName = (irai.patientLastName || '') + ' ' + (irai.patientFirstName || '');

        if (Number(irai.statusId) >= 4 && Number(irai.statusId) <= 26) {
          irai.status = "受付済";
          return irai;
        }

        //masterStatusからステータスの表示名を取得して設定
        const statusObj = statusMaster.current.find(
          (status) => status.statusId === irai.statusId
        );
        return {
          ...irai,
          status: statusObj ? statusObj.status : "",
        };
      });
      irais.current = mergedIraiData;

      // データ更新後に再レンダリングを強制
      setUpdateCounter(prev => prev + 1);
    } finally {
      // 実行完了フラグをリセット
      isRefreshingRef.current = false;
    }
  }, [auth, showToast]); // 依存配列を更新

  // 個別のIraiをキャッシュから削除する関数
  const removeIraiFromCache = useCallback((iraiId: string) => {
    irais.current = irais.current.filter(irai => irai.id !== iraiId);
    // 再レンダリングを強制するためにカウンターを更新
    setUpdateCounter(prev => prev + 1);
  }, []);

  // getMaster関数を実装
  const getMaster = useCallback((fieldName: keyof Irai): MasterItem[] => {
    switch (fieldName) {
      case "diagnosisId":
        return diagnosisMaster.current;
      case "patientGender":
        return genderMaster.current;
      case "lymphStsId":
        return lympthMaster.current;
      case "esStsId":
        return erMaster.current;
      case "prStsId":
        return pgrMaster.current;
      case "her2StsId":
        return her2StatusMaster.current;
      case "tclass":
        return tClassMaster.current;
      case "nclass":
        return nClassMaster.current;
      case "mclass":
        return mClassMaster.current;
      case "menstruation":
        return menstruationMaster.current;
      case "tumorGrade":
        return tumorGradeMaster.current;
      case "biopsyId":
        return biopsyMaster.current;
      default:
        return [];
    }
  }, [diagnosisMaster, genderMaster, lympthMaster, erMaster, pgrMaster, her2StatusMaster, tClassMaster, nClassMaster, mClassMaster, menstruationMaster, tumorGradeMaster, biopsyMaster]);

  // updateCounterに基づいてiraiDataを計算
  const iraiData = [...irais.current];

  return (
    <DataContext.Provider
      value={{
        iraiData: iraiData,
        refreshData: refresh,
        removeIraiFromCache: removeIraiFromCache,
        getMaster: getMaster,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
